import React, { useEffect, useState } from "react";
import { ArrowRight, Menu, X, Phone } from "lucide-react";
import { navLinks, clinicInfo } from "../mock";
import Logo from "./Logo";

// Anchor hrefs to observe for active state
const SECTION_HREFS = ["#home", "#about", "#services", "#doctors", "#news", "#contact"];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeHash, setActiveHash] = useState("#home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Active section tracking via IntersectionObserver
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Only run on pages that actually have these anchor sections (home page)
    const targets = SECTION_HREFS
      .map((h) => document.getElementById(h.slice(1)))
      .filter(Boolean);
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersection ratio that's intersecting
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) {
          setActiveHash(`#${visible.target.id}`);
        }
      },
      {
        // Trigger when ~35% of section is in viewport (accounts for sticky header)
        rootMargin: "-25% 0px -50% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1]
      }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  const isActive = (href) => {
    // Only home page anchor links are highlighted based on scroll.
    if (!href.includes("#")) return false;
    // Extract just the hash portion
    const hash = href.slice(href.indexOf("#"));
    return hash === activeHash;
  };

  return (
    <div className="sticky top-0 z-50 w-full">
      {/* Top announcement bar — collapses on scroll */}
      <div
        className={`w-full bg-[#1a2464] text-white overflow-hidden transition-[max-height,opacity,padding] duration-300 ease-out ${
          scrolled ? "max-h-0 opacity-0 py-0" : "max-h-16 opacity-100 py-2"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-4 md:px-8 flex flex-wrap items-center justify-center md:justify-between gap-x-6 gap-y-1 text-[12px] md:text-[13px]">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded-full bg-[#3dc9b3] grid place-items-center shrink-0">
              <svg viewBox="0 0 24 24" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="5 12 10 17 20 7" />
              </svg>
            </span>
            <span className="font-semibold">100% Bulk Billed</span>
            <span className="text-white/70 hidden sm:inline">with a valid Medicare card</span>
          </div>
          <div className="hidden md:flex items-center gap-5 text-white/80">
            <a href={`tel:${clinicInfo.phoneRaw}`} className="hover:text-[#3dc9b3] inline-flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" /> {clinicInfo.phone}
            </a>
            <span className="text-white/40">·</span>
            <span>Mon–Fri 8am–5pm · Sun 8am–12pm</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <header
        className={`w-full px-4 md:px-8 transition-[padding,background] duration-300 ${
          scrolled ? "pt-2 pb-2 bg-white/70 backdrop-blur-xl shadow-[0_8px_24px_-16px_rgba(26,36,100,0.35)]" : "pt-4 pb-1 bg-transparent"
        }`}
      >
        <nav
          className={`mx-auto max-w-[1400px] flex items-center justify-between px-5 md:px-6 rounded-full transition-all duration-300 ${
            scrolled
              ? "bg-white shadow-md py-2 md:py-2.5"
              : "bg-white/95 shadow-md py-2.5 md:py-3"
          }`}
        >
          <a href="/" className="flex items-center gap-3 shrink-0">
            <Logo variant="dark" size={scrolled ? "sm" : "md"} />
          </a>

          <ul className="hidden lg:flex items-center gap-8 xl:gap-9">
            {navLinks.map((l) => {
              const active = isActive(l.href);
              return (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className={`relative font-semibold text-[13px] tracking-[0.08em] uppercase transition-colors after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:bg-[#3dc9b3] after:transition-all ${
                      active
                        ? "text-[#3dc9b3] after:w-full"
                        : "text-[#1a2464] hover:text-[#3dc9b3] after:w-0 hover:after:w-full"
                    }`}
                  >
                    {l.label}
                  </a>
                </li>
              );
            })}
          </ul>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${clinicInfo.phoneRaw}`}
              className="hidden xl:flex items-center gap-2 text-[#1a2464] font-semibold text-sm hover:text-[#3dc9b3]"
            >
              <Phone className="w-4 h-4" /> {clinicInfo.phone}
            </a>
            <a
              href={clinicInfo.hotdocUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 bg-[#1a2464] text-white font-semibold text-sm pl-5 pr-2 py-2 rounded-full hover:bg-[#141c52] transition-colors"
            >
              <span>Book on HotDoc</span>
              <span className="w-7 h-7 rounded-full bg-[#3dc9b3] grid place-items-center group-hover:translate-x-0.5 transition-transform">
                <ArrowRight className="w-3.5 h-3.5 text-white" />
              </span>
            </a>
          </div>

          <button
            className="lg:hidden text-[#1a2464] p-2"
            onClick={() => setOpen(!open)}
            aria-label="toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </nav>
      </header>

      {/* Mobile menu */}
      <div
        onClick={() => setOpen(false)}
        className={`lg:hidden fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        style={{ top: 0 }}
      />
      <div
        className={`lg:hidden absolute left-4 right-4 mt-2 bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-3 pointer-events-none"
        }`}
      >
        <ul className="flex flex-col p-4">
          {navLinks.map((l) => {
            const active = isActive(l.href);
            return (
              <li key={l.label}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className={`block font-semibold px-4 py-3 rounded-xl ${
                    active ? "bg-[#3dc9b3]/10 text-[#3dc9b3]" : "text-[#1a2464] hover:bg-[#f0edfa]"
                  }`}
                >
                  {l.label}
                </a>
              </li>
            );
          })}
          <li className="mt-2 pt-3 border-t border-[#eee6f7] flex flex-col gap-2 px-2 pb-2">
            <a
              href={`tel:${clinicInfo.phoneRaw}`}
              className="inline-flex items-center gap-2 text-[#1a2464] font-semibold px-4 py-2"
            >
              <Phone className="w-4 h-4" /> {clinicInfo.phone}
            </a>
            <a
              href={clinicInfo.hotdocUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center gap-2 bg-[#1a2464] text-white font-semibold px-5 py-3 rounded-full"
            >
              Book on HotDoc
              <ArrowRight className="w-4 h-4" />
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
}
